import React, { PropsWithChildren } from 'react'
import { withStyles, createStyles } from '@mui/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { Routes, BrowserRouter, Route } from 'react-router-dom'
import { I18nextProvider, useTranslation } from 'react-i18next'
import { ExcelGeneratorContainer } from './infrastructure/excelExport'
import { FeatureContainer } from './infrastructure/feature'
import { LoaderStatusContainer } from './infrastructure/loader'
import Shell from './infrastructure/shell/shell'
import { SnackbarContainer } from './infrastructure/snackbars'
import { Startup } from './infrastructure/startup'
import { NotificationContainer } from './infrastructure/shell/notifications/container'
import { UserContextContainer } from './infrastructure/signIn/userContext'
import { UserSettingsContextContainer } from './infrastructure/shell/userSettings/userSettingsStore'
import { UserSettingsDialog } from './infrastructure/shell/userSettings/userSettingsDialog'
import { NavigationTrackerContainer } from './infrastructure/navigation'
import i18nInstance from './infrastructure/i18nextHelper'
import { initTFunction } from './infrastructure/i18nextHelper'
import { routes, RouteWrapper } from './infrastructure/router'
import { useDomStateTracker } from './infrastructure/domStateTracker'
import { AccountingPeriodDialog } from './app/stock/actualStockEdit/accountingPeriod/accountingPeriodDialog'
import { ActualStockContainer } from './app/stock/actualStockEdit/actualStockStore'
import { AssignableMovementsDialog } from './app/deals/dealEdit/assignableMovements'
import { CommentButtonDialog } from './app/common/comment/commentButtonDialog'
import { CommentContainer } from './app/common/comment/commentStore'
import { CreateVesselTransportContainer } from './app/vessels/createVesselTransport'
import { DealContainer, DealDialogContainer } from './app/deals/dealEdit/dealEditStore'
import DealEdit from './app/deals/dealEdit/dealEdit'
import RecipeEdit from './app/recipes/recipeEdit'
import { RecipeEditContainer } from './app/recipes/recipeEditStore'
import { ForecastContainer } from './app/mktSales/forecastEdit/forecastEditStore'
import { HistoryDialog } from './app/common/history/dialog'
import { ImportDialog } from './app/common/import/dialog'
import { LinkVesselMovementContainer } from './app/stock/movementEdit/movementForms/linkVesselContainer'
import { MktSalesContainer } from './app/mktSales/mktSalesStore'
import { MovementMessageContainer } from './app/stock/movementMessageStore'
import { MovementMessageHistory } from './app/stock/movementEdit/movementMessageHistory'
import { PoReleaseDetailsDialog } from './app/stock/movementEdit/movementForms/sap/createInSap/releaseDialog'
import { PriceIndexDetailsDialog } from './app/pricings/priceIndexDetailsDialog'
import { PricingDialog } from './app/pricings/pricingDialog'
import { PricingDialogContainer } from './app/pricings/pricingDialog'
import { StockBoardContainer } from './app/stock/stockBoardStore'
import { SimulationBoardContainer } from './app/stock/simulation/simulationBoardStore'
import { StockFiltersContainer } from './app/stock/filters/filtersStore'
import { StockMovementContainer } from './app/stock/movementEdit/stockMovementStore'
import StockMovementEdit from './app/stock/movementEdit/stockMovementEdit'
import { StockProjectionDetailDialog } from './app/stock/stockProjectionDetailDialog'
import { SupplyBalanceStore } from './app/supplyBalance/supplyBalanceStore'
import { TransitStockDetailDialog } from './app/stock/transitStockDetailDialog'
import { TruckTransportContainer } from './app/truck/truckStore'
import { TruckTransportForm } from './app/truck/form/truckForm'
import { VesselContainer } from './app/vessels/vesselStore'
import VesselEdit from './app/vessels/vesselEdit/vesselEdit'
import { VesselEditContainer, VesselEditDialogContainer } from './app/vessels/vesselEdit/vesselEditStore'
import { PurchaseDealDetailDialog } from './app/stock/purchaseDealDetailDialog'
import { MktSalesFiltersContainer } from './app/mktSales/mktSalesFiltersStore'
import { VesselNavigationDialog } from './app/deals/dealEdit/vesselNavigationDialog'

function _App() {
    let domStateTracker = useDomStateTracker()
    const { t, i18n } = useTranslation()
    initTFunction(t)

    return (
        <I18nextProvider i18n={i18nInstance}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <CustomProviders>
                    <div id='app'>
                        <div id='page'>
                            <Shell>
                                <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                                    <Routes>
                                        {routes.map((route, i) =>
                                            <Route key={i} path={route.path} element={<RouteWrapper route={route} />} />)}
                                    </Routes>
                                </BrowserRouter>
                            </Shell>
                            <VesselEdit />
                            <StockMovementEdit />
                            <DealEdit />
                            <TruckTransportForm />
                            <HistoryDialog />
                            <MovementMessageHistory />
                            <UserSettingsDialog />
                            <ImportDialog />
                            <PricingDialog />
                            <PriceIndexDetailsDialog />
                            <StockProjectionDetailDialog />
                            <AccountingPeriodDialog />
                            <TransitStockDetailDialog />
                            <PoReleaseDetailsDialog />
                            <AssignableMovementsDialog />
                            <CommentButtonDialog />
                            <PurchaseDealDetailDialog />
                            <VesselNavigationDialog />
                            <RecipeEdit />
                            <Startup />
                        </div>
                        <div id={'dom-status-' + domStateTracker.status} style={{ position: 'absolute', zIndex: -15 }}></div>
                    </div>
                </CustomProviders>
            </LocalizationProvider>
        </I18nextProvider>
    )
}

let containers = [
    FeatureContainer,
    UserContextContainer,
    LoaderStatusContainer,
    SnackbarContainer,
    NavigationTrackerContainer,
    DealDialogContainer,
    RecipeEditContainer,
    DealContainer,
    PricingDialogContainer,
    VesselEditDialogContainer,
    VesselEditContainer,
    VesselContainer,
    StockFiltersContainer,
    StockBoardContainer,
    SimulationBoardContainer,
    ActualStockContainer,
    StockMovementContainer,
    MktSalesFiltersContainer,
    MktSalesContainer,
    TruckTransportContainer,
    LinkVesselMovementContainer,
    ForecastContainer,
    SupplyBalanceStore,
    NotificationContainer,
    CreateVesselTransportContainer,
    ExcelGeneratorContainer,
    UserSettingsContextContainer,
    MovementMessageContainer,
    CommentContainer
]

function CustomProviders(props: PropsWithChildren<{}>) {
    return <>{containers.reduceRight((acc, cur) => <cur.Provider>{acc}</cur.Provider>, props.children)}</>
}

let styles = theme =>
    createStyles({
        '@global': {
            html: {
                fontSize: 16,
                [theme.breakpoints.up('sm')]: {
                    fontSize: 10
                },
                [theme.breakpoints.up(900)]: {
                    fontSize: 12
                },
                [theme.breakpoints.up(1400)]: {
                    fontSize: 16
                }
            },
            'html, body': {
                height: '100vh',
                width: '100vw',
                margin: 0,
                padding: 0,
                boxSizing: 'border-box'
            },
            '#appContainer, #app, #page, #page>div': {
                height: '100%'
            },
            '.MuiDialog-root, .MuiBackdrop-root': {
                marginTop: '2.5em !important'
            },
            '.MuiDialog-root .MuiAppBar-root': {
                position: 'sticky !important'
            },
            '*, *::before, *::after': {
                boxSizing: 'border-box'
            }
        }
    })

export default withStyles(styles)(_App)
